import { type FC, useCallback, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";
import { Alert } from "@snapchat/snap-design-system";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { type AbsoluteRoute, type RouteParams } from "../../../core/routes";
import { useQueryParams } from "../../../core/routes/hooks";
import { CofConfigId } from "../../../gql-generated/types";
import { PageContent, PageContentSpinner } from "../../../layout/components";
import { LensUpdateAtDate } from "../../../lens/components/shared";
import { UserInfoContext } from "../../../shared/api/contexts";
import { Flexbox } from "../../../shared/components";
import {
    LensFoldersSearchParams,
    SearchParamKeys,
    useSearchParams,
    useSelectedLensFolder,
    useSelectedLensFolderV3,
    useSelectedOrganizationContainer,
} from "../../../shared/hooks";
import { getCurrentDateAndTimeInSeconds } from "../../../shared/utils/date";
import {
    type GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2Query,
    type GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2QueryVariables,
    type GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQuery,
    type GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQueryVariables,
    getAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2,
    getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation,
} from "../../api/queries";
import {
    useLensSpaceOptions,
    useLensSpaceOptionsV2,
    useOrganizationOptions,
    useOrganizationOptionsV2,
} from "../../hooks";
import { LensNameText, LensSubmissionDisclaimer, lensDetailsContainer } from "../lens-details/shared";
import { LensDetailsIcon } from "../shared";

import { CreateOrganizationOrLensFolderLink } from "./CreateOrganizationOrLensFolderLink";
import { SelectLensSpaceDropdownInputSection, SelectOrganizationDropdownInputSection } from "./DropdownInputSections";
import { LensSubmissionFlowPageHeader } from "./LensSubmissionFlowPageHeader";
import { SelectLensFolderActions } from "./SelectLensFolderActions";
import {
    LensInformationTextContainer,
    SelectLensFolderPageContainer,
    lensInformationContainer,
    lensSubmissionDisclaimer,
} from "./SelectLensFolderPage.styles";
import { LensInformation, SelectLensFolderHeader, SelectLensFolderSubHeader } from "./messages.generated";

export const SelectLensFolderPage: FC = () => {
    const { submissionId = "" } = useParams<RouteParams[AbsoluteRoute.SUBMIT_LENS_FOLDER_SELECT]>();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const {
        loading: loadingV1,
        data: allOrgsDataV1,
        error: errorV1,
    } = useQuery<
        GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQuery,
        GetAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreationQueryVariables
    >(getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation, {
        variables: { submissionId },
        skip: isMyLensesOrgsV2Enabled,
    });

    const {
        loading: v2Loading,
        data: dataV2,
        error: v2Error,
    } = useQuery<
        GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2Query,
        GetAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2QueryVariables
    >(getAllPreSubmissionInfoWithOrgsForLensSpaceCreationV2, {
        variables: { submissionId, limit: 100, cursor: "" },
        skip: !isMyLensesOrgsV2Enabled,
    });

    const organizationsForLensSpaceCreation = dataV2?.myLensesOrganizationsForLensSpaceCreation.organizationList ?? [];

    const error = isMyLensesOrgsV2Enabled ? v2Error : errorV1;

    const searchParams = useSearchParams();
    const organizationId = searchParams.get(SearchParamKeys.Organization) ?? undefined;

    const { orgId: selectedOrganizationId } = useSelectedOrganizationContainer();

    const userInfo = useContext(UserInfoContext);

    const organizationOptionsV1 = useOrganizationOptions(
        allOrgsDataV1,
        userInfo?.data?.getUserProfile.defaultOrganizationId ?? "",
    );

    const lensFolderOptionsV1 = useLensSpaceOptions(allOrgsDataV1, organizationId ?? "");

    const selectedLensFolderV3 = useSelectedLensFolderV3();
    const { lensFolder: selectedLensFolder } = useSelectedLensFolder();

    const selectedLensFolderId = isMyLensesOrgsV2Enabled ? selectedLensFolder?.id : selectedLensFolderV3?.id;

    const organizationOptionsV2 = useOrganizationOptionsV2({ organizations: organizationsForLensSpaceCreation });

    const organizationOptions = isMyLensesOrgsV2Enabled ? organizationOptionsV2 : organizationOptionsV1;

    const {
        options: lensFolderOptionsV2,
        loading: lensFoldersLoading,
        error: lensFoldersError,
    } = useLensSpaceOptionsV2();

    const lensFolderOptions = isMyLensesOrgsV2Enabled ? lensFolderOptionsV2 : lensFolderOptionsV1;

    const { updateQueryParams } = useQueryParams();

    const setSelectedLensFolder = useCallback(
        (lensFolderId: string) => {
            updateQueryParams({ [LensFoldersSearchParams.LensFolder]: lensFolderId });
        },
        [updateQueryParams],
    );

    const setSelectedOrganization = useCallback(
        (selectedOrgId: string) => {
            updateQueryParams({
                [SearchParamKeys.Organization]: selectedOrgId,
                [LensFoldersSearchParams.LensFolder]: "",
            });
        },
        [updateQueryParams],
    );

    const selectedOrganizationOption = useMemo(() => {
        return organizationOptions.find((option) => option.value === selectedOrganizationId);
    }, [selectedOrganizationId, organizationOptions]);

    const selectedLensFolderOption = useMemo(() => {
        return lensFolderOptions.find((option) => option.value === selectedLensFolderId);
    }, [selectedLensFolderId, lensFolderOptions]);

    const isLoadingV1 = loadingV1 || !allOrgsDataV1 || userInfo?.loading;
    const isLoadingV2 = v2Loading || !dataV2;

    if (isMyLensesOrgsV2Enabled ? isLoadingV2 : isLoadingV1) {
        return <PageContentSpinner />;
    }

    if (error) {
        throw error;
    }
    if (lensFoldersError) {
        throw lensFoldersError;
    }

    const getPreSubmissionInfo = isMyLensesOrgsV2Enabled
        ? dataV2?.getPreSubmissionInfo
        : allOrgsDataV1?.getPreSubmissionInfo;

    if (!getPreSubmissionInfo) {
        return <PageContentSpinner />;
    }

    const organizationsAvailableForLensSpaceCreation = isMyLensesOrgsV2Enabled
        ? organizationsForLensSpaceCreation
        : allOrgsDataV1?.getMyLensesOrganizationsAvailableForLensSpaceCreation.organizationsList ?? [];

    // validationErrors defaults to [] to survive a stale Apollo cache entry normalized before the
    // field was added to the fragment (deserializes as undefined despite the non-null schema type) —
    // same guard as getLensSubmissionRouteV3.
    const { iconUrl, name: lensName, surfaceType, validationErrors = [] } = getPreSubmissionInfo;
    const hasValidationErrors = validationErrors.length > 0;

    return (
        <PageContent>
            {hasValidationErrors && (
                <Flexbox flexDirection="column" gap="8px" style={{ marginBottom: "16px" }}>
                    {validationErrors.map((error, index) => (
                        <Alert key={index} status="error" title={error.title} description={error.message} />
                    ))}
                </Flexbox>
            )}
            <LensSubmissionFlowPageHeader
                headerText={<SelectLensFolderHeader />}
                subheaderText={<SelectLensFolderSubHeader />}
            />
            <SelectLensFolderPageContainer>
                <LensInformationTextContainer>
                    <LensInformation />
                </LensInformationTextContainer>

                <Flexbox className={lensInformationContainer}>
                    <div>
                        <LensDetailsIcon iconUrl={iconUrl} />
                    </div>
                    <Flexbox justifyContent="center" flexDirection="column" className={lensDetailsContainer}>
                        <LensNameText>{lensName}</LensNameText>
                        <LensUpdateAtDate seconds={getCurrentDateAndTimeInSeconds()}></LensUpdateAtDate>
                    </Flexbox>
                </Flexbox>
                <SelectOrganizationDropdownInputSection
                    onChange={setSelectedOrganization}
                    options={organizationOptions}
                    defaultOption={selectedOrganizationOption}
                    isDisabled={hasValidationErrors}
                />
                <SelectLensSpaceDropdownInputSection
                    onChange={setSelectedLensFolder}
                    options={lensFolderOptions}
                    value={selectedLensFolderOption}
                    isDisabled={hasValidationErrors}
                    isLoading={isMyLensesOrgsV2Enabled && lensFoldersLoading}
                />
                <CreateOrganizationOrLensFolderLink
                    hasOrganizationsAvialableForLensFolderCreation={
                        organizationsAvailableForLensSpaceCreation.length > 0
                    }
                    isDisabled={hasValidationErrors}
                />
                <SelectLensFolderActions
                    selectedLensFolderId={selectedLensFolderId ?? ""}
                    surfaceType={surfaceType}
                    isDisabled={hasValidationErrors}
                />
            </SelectLensFolderPageContainer>

            <Flexbox justifyContent="center">
                <LensSubmissionDisclaimer className={lensSubmissionDisclaimer} />
            </Flexbox>
        </PageContent>
    );
};
